import { useState, type ReactNode } from 'react';
import { useGT } from 'gt-react';
import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { matchesSourceSearch, type SourceChoice } from './sourceOverride';

/**
 * A searchable list of the warehouses a line can transfer from (GEN-3148).
 * Rexel has hundreds of locations and several share a name (the Londons), so
 * each row carries the short location code, the division and the on-hand
 * quantity, in the order the lookup returns them (most stock first). Typing
 * filters on code, name and division without reordering.
 */
export function TransferSourcePicker({
  choices,
  value,
  open: controlledOpen,
  onOpenChange,
  onSelect,
  isLoading,
  disabled,
  ariaLabel,
  triggerLabel,
  triggerClassName,
  leadingChoice,
}: {
  choices: readonly SourceChoice[];
  /** The selected warehouse id, or the leading choice's value. */
  value: string;
  /** Controlled open state, for callers that load the choices on open. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSelect: (next: string) => void;
  /** The warehouses are still loading: shown below whatever is listed. */
  isLoading: boolean;
  disabled?: boolean;
  ariaLabel: string;
  /** What the closed picker shows. */
  triggerLabel: ReactNode;
  triggerClassName?: string;
  /**
   * A fixed first row that is not a warehouse (the cart's vendor on the
   * board). Disabled while whatever picking it needs has not loaded.
   */
  leadingChoice?: { value: string; label: string; disabled: boolean };
}) {
  const gt = useGT();
  const [internalOpen, setInternalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const open = controlledOpen ?? internalOpen;
  const setOpen = (next: boolean) => {
    setInternalOpen(next);
    onOpenChange?.(next);
    if (!next) setSearch('');
  };
  const visible = choices.filter((choice) =>
    matchesSourceSearch(choice, search),
  );
  const leadingVisible =
    leadingChoice != null &&
    matchesSourceSearch(
      {
        warehouseCode: null,
        warehouseName: leadingChoice.label,
        divisionCode: null,
        divisionName: null,
      },
      search,
    );

  const choose = (next: string) => {
    setOpen(false);
    onSelect(next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={open}
        role="combobox"
        onClick={(event) => event.stopPropagation()}
        className={cn(
          'border-input flex items-center justify-between gap-2 rounded-md border bg-transparent text-sm whitespace-nowrap shadow-xs outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50',
          triggerClassName,
        )}
      >
        <span className="min-w-0 truncate">{triggerLabel}</span>
        <CaretDownIcon className="size-4 shrink-0 opacity-50" />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        // Wide enough for `CODE  Name · Division  1,234 on hand` when the
        // division has no short code and falls back to its name.
        className="w-96 p-0"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Filtering is ours: cmdk's own scorer would reorder the list,
            and the buyer wants the most stock to stay on top. */}
        <Command shouldFilter={false}>
          <CommandInput
            autoFocus
            value={search}
            onValueChange={setSearch}
            placeholder={gt('Search by code, name or division…', {
              context: 'transfer source picker search placeholder',
            })}
          />
          <CommandList>
            {!isLoading && (
              <CommandEmpty>
                {gt('No warehouse matches.', {
                  context: 'transfer source picker, search found nothing',
                })}
              </CommandEmpty>
            )}
            <CommandGroup>
              {leadingChoice != null && leadingVisible && (
                <CommandItem
                  value={leadingChoice.value}
                  disabled={leadingChoice.disabled}
                  onSelect={choose}
                >
                  <SelectedMark selected={value === leadingChoice.value} />
                  <span className="truncate">{leadingChoice.label}</span>
                </CommandItem>
              )}
              {visible.map((choice) => (
                <SourceChoiceItem
                  key={choice.warehouseId}
                  choice={choice}
                  selected={value === choice.warehouseId}
                  onSelect={choose}
                />
              ))}
            </CommandGroup>
            {isLoading && (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                {gt('Loading warehouses…', {
                  context: 'source picker, warehouses with stock are loading',
                })}
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function SelectedMark({ selected }: { selected: boolean }) {
  return (
    <CheckIcon
      className={cn('size-4', selected ? 'opacity-100' : 'opacity-0')}
    />
  );
}

/** One warehouse row: the label on the left, its on-hand stock on the right. */
function SourceChoiceItem({
  choice,
  selected,
  onSelect,
}: {
  choice: SourceChoice;
  selected: boolean;
  onSelect: (warehouseId: string) => void;
}) {
  const gt = useGT();
  return (
    <CommandItem value={choice.warehouseId} onSelect={onSelect}>
      <SelectedMark selected={selected} />
      <SourceChoiceLabel choice={choice} />
      {choice.onHandQuantity != null && (
        <span className="ml-auto shrink-0 pl-2 text-xs tabular-nums text-muted-foreground">
          {gt('{count} on hand', {
            context: 'source picker option, stock at a warehouse',
            count: choice.onHandQuantity.toLocaleString(),
          })}
        </span>
      )}
    </CommandItem>
  );
}

/** `RDLON  London · RA`, or just the name when the location has no code. */
function SourceChoiceLabel({ choice }: { choice: SourceChoice }) {
  const detail = [
    choice.warehouseCode != null ? choice.warehouseName : null,
    choice.divisionCode ?? choice.divisionName,
  ].filter((part): part is string => part != null && part !== '');
  return (
    <span className="flex min-w-0 items-baseline gap-1.5">
      <span className="shrink-0 font-mono text-xs font-medium">
        {choice.warehouseCode ?? choice.warehouseName}
      </span>
      {detail.length > 0 && (
        <span className="truncate text-xs text-muted-foreground">
          {detail.join(' · ')}
        </span>
      )}
    </span>
  );
}
